import type {
  FetchCartTopperRowProps,
  FetchRecommendedPDPRowProps,
  FetchRecommendedSortProps,
  JaneDM,
  SmartSort,
  SmartSortProduct,
} from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/data-access';

import { useSDK } from '../sdkProvider';

export interface UseFetchPlacement<
  ProductShape extends object = AlgoliaProduct,
> {
  dynamicPlacement?: SmartSort<ProductShape>;
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  instance: SmartSort<ProductShape> | undefined;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  numHits: number;
  /**
   * @deprecated Use `instance.products` instead
   */
  products: SmartSortProduct<ProductShape>[];
  searchResultFacets: Record<string, Record<string, number>>;
}

export type UseFetchPlacementProps<T = unknown> = T & {
  dependencies: (string | number | undefined | number[])[];
  enabled?: boolean;
};

export const useCartTopperRow = <ProductShape extends object = AlgoliaProduct>(
  props: Omit<
    UseFetchPlacementProps<FetchCartTopperRowProps>,
    'dependencies' | 'cartProductIds'
  > & { cartProductIds: number[] }
): UseFetchPlacement<ProductShape> => {
  const { storeId, cartProductIds, searchAttributes } = props;

  const dependencies = useMemo(
    () => [storeId, cartProductIds],
    [storeId, cartProductIds]
  );

  const fetchPlacement = useCallback(async (sdk: JaneDM) => {
    return await sdk.fetchCartTopperRow<ProductShape>({
      cartProductIds,
      searchAttributes,
      storeId,
    });
  }, dependencies);

  const enabled = useMemo(() => cartProductIds.length !== 0, dependencies);

  return useFetchPlacement({ dependencies, enabled }, fetchPlacement);
};

export const useRecommendedSort = <
  ProductShape extends object = AlgoliaProduct,
>(
  props: Omit<UseFetchPlacementProps<FetchRecommendedSortProps>, 'dependencies'>
): UseFetchPlacement<ProductShape> => {
  const {
    enabled,
    disableAds,
    maxProducts,
    numColumns,
    pageSize,
    searchAttributes,
    searchFacets = [
      'activities',
      'aggregate_rating',
      'applicable_special_ids',
      'available_weights',
      'brand_subtype',
      'brand',
      'bucket_price',
      'category',
      'feelings',
      'has_brand_discount',
      'kind',
      'percent_cbd',
      'percent_thc',
      'root_types',
      'compound_names',
    ],
    searchFilter,
    searchQuery = '',
    searchSort,
    storeId,
  } = props;

  const dependencies = [
    maxProducts,
    storeId,
    searchFilter,
    searchQuery,
    searchSort,
  ];

  const fetchPlacement = useCallback(
    async (sdk: JaneDM) =>
      await sdk.fetchRecommendedSort<ProductShape>({
        disableAds,
        maxProducts,
        numColumns,
        pageSize,
        searchAttributes,
        searchFacets,
        searchFilter,
        searchQuery,
        searchSort,
        storeId: Number(storeId),
      }),
    dependencies
  );

  return useFetchPlacement({ dependencies, enabled }, fetchPlacement);
};

export const useSimilarProductsRow = <
  ProductShape extends object = AlgoliaProduct,
>(
  props: Omit<
    UseFetchPlacementProps<FetchRecommendedPDPRowProps>,
    'dependencies' | 'productId' | 'posProductId'
  > & { productId: number }
): UseFetchPlacement<ProductShape> => {
  const { storeId, productId, searchAttributes } = props;

  const dependencies = [productId];

  const fetchPlacement = useCallback(
    async (sdk: JaneDM) =>
      await sdk.fetchRecommendedPDPRow<ProductShape>({
        productId,
        searchAttributes,
        storeId,
      }),
    dependencies
  );

  return useFetchPlacement({ dependencies }, fetchPlacement);
};

export const useFetchPlacement = <ProductShape extends object = AlgoliaProduct>(
  props: UseFetchPlacementProps,
  fetchPlacement: (sdk: JaneDM) => Promise<SmartSort<ProductShape>>
): UseFetchPlacement<ProductShape> => {
  const { enabled = true } = props;

  const { sdk } = useSDK();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [instance, setInstance] = useState<SmartSort<ProductShape> | undefined>(
    undefined
  );

  const fetchPlacements = useCallback(async () => {
    if (!sdk) {
      setIsLoading(false);
      setIsError(false);
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    setInstance(undefined);

    try {
      const response = enabled ? await fetchPlacement(sdk) : undefined;
      setInstance(response);
      setIsSuccess(true);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [fetchPlacement, enabled, sdk]);

  const fetchNextPage = useCallback(async () => {
    if (instance && instance.hasNextPage()) {
      setIsLoading(true);
      setIsError(false);
      setIsSuccess(false);
      try {
        await instance.nextPage();
        setIsSuccess(true);
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    }
  }, [instance]);

  useEffect(() => {
    fetchPlacements();
  }, [fetchPlacements]);

  const response = useMemo(
    () => ({
      fetchNextPage,
      hasNextPage: instance?.hasNextPage() ?? false,
      instance,
      isError,
      isLoading,
      isSuccess,
      numHits: instance?.totalProducts ?? 0,
      products: instance?.products ?? [],
      searchResultFacets: instance?.searchFacets ?? {},
    }),
    [instance, isError, isLoading, isSuccess, fetchNextPage]
  );

  return response;
};
